import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, CalendarCheck, Camera } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ReaderCard } from "@/components/ReaderCard";
import { E, EImg } from "@/components/Editable";
import { useAuth } from "@/context/AuthContext";

function nextDays(n) {
  const days = [];
  const d = new Date();
  for (let i = 1; i <= n; i++) {
    const dt = new Date(d);
    dt.setDate(d.getDate() + i);
    days.push(dt);
  }
  return days;
}

export default function BookingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [slots, setSlots] = useState([]);
  const [readers, setReaders] = useState([]);
  const [readerId, setReaderId] = useState(null);
  const [upi, setUpi] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]);
  const days = nextDays(14);

  const refreshAvailability = () => {
    if (!readerId || !date) return;
    api.get(`/bookings/availability?reader_id=${readerId}&date=${date.toISOString().slice(0, 10)}`)
      .then(({ data }) => setBookedTimes(data.booked))
      .catch(() => setBookedTimes([]));
  };

  useEffect(() => {
    if (!readerId || !date) { setBookedTimes([]); return; }
    refreshAvailability();
  }, [readerId, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (time && bookedTimes.includes(time)) setTime(null);
  }, [bookedTimes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/bookings/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/bookings/slots").then(({ data }) => setSlots(data)).catch(() => {});
    api.get("/readers").then(({ data }) => setReaders(data)).catch(() => {});
    api.get("/payments/config").then(({ data }) => setUpi(data.upi)).catch(() => {});
  }, []);

  const checkout = async () => {
    if (!user) {
      navigate("/auth", { state: { from: "/book" } });
      return;
    }
    if (!pkg || !readerId || !date || !time) {
      toast.error("Choose a package, reader, date and time first.");
      return;
    }
    if (!screenshotFile) {
      toast.error("Please attach your UPI payment screenshot — it's required to confirm the booking.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("package_id", pkg);
      fd.append("reader_id", readerId);
      fd.append("date", date.toISOString().slice(0, 10));
      fd.append("time", time);
      fd.append("notes", notes);
      fd.append("screenshot", screenshotFile);
      const { data } = await api.post("/bookings/upi", fd, { headers: { "Content-Type": "multipart/form-data" } });
      navigate(`/booking/success?session_id=${data.session_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 409) {
        setTime(null);
        refreshAvailability();
      }
      setLoading(false);
    }
  };

  const inrDisc = (p) => (p.price_inr_discounted ?? p.price_inr)?.toLocaleString("en-IN");
  const inrFull = (p) => p.price_inr?.toLocaleString("en-IN");

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-10 max-w-6xl mx-auto" data-testid="booking-page">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-4"><E k="booking_overline" f="Live 1:1 Tarot Counselling · Mumbai" /></p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
        <E k="booking_title" f="Genuine guidance." /> <span className="gold-text italic"><E k="booking_title_accent" f="Zero fluff." /></span>
      </h1>
      <p className="mt-5 text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed" data-testid="booking-hero-message">
        <E k="booking_hero_message" f="This is tarot counselling, not fortune-telling theatre. Our three trained readers — with 28+ combined years of practice and over 22,000 sessions between them — give you honest, specific guidance you can actually act on. Every call is private, judgement-free, and ends with clear next steps. And if the cards can't answer your question, we'll say so plainly. That's the promise." />
      </p>
      <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid="booking-trust-strip">
        {[
          "22,000+ readings delivered",
          "Certified, trained practitioners",
          "100% private & confidential",
          "Full refund up to 24h before",
        ].map((item, i) => (
          <span key={item} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-foreground/80">
            <Check size={13} weight="bold" className="text-gold" /> <E k={`booking_trust_${i + 1}`} f={item} />
          </span>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {packages.map((p, i) => (
          <motion.div
            key={p.id}
            role="button"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            onClick={() => setPkg(p.id)}
            data-testid={`package-card-${p.id}`}
            className={`cursor-pointer text-left p-8 border transition-colors duration-300 hover-lift ${
              pkg === p.id ? "border-gold bg-gold/5" : "border-white/10 bg-card"
            } ${i === 1 ? "md:-translate-y-3" : ""}`}
          >
            {i === 1 && <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-3"><E k="booking_most_chosen" f="Most chosen" /></p>}
            <h3 className="font-heading text-2xl font-semibold tracking-tight"><E k={`pkg_${p.id}_name`} f={p.name} /></h3>
            <p className="text-xs text-muted-foreground mt-1"><E k={`pkg_${p.id}_tagline`} f={p.tagline} /></p>
            <p className="mt-4">
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1 bg-gold/15 text-gold border border-gold/40" data-testid={`discount-badge-${p.id}`}>
                <E k={`pkg_${p.id}_discount_badge`} f={`First booking −${p.discount_pct || 50}%`} />
              </span>
            </p>
            <p className="mt-3">
              <span className="font-heading text-4xl font-bold text-gold">₹{inrDisc(p)}</span>
              <span className="text-sm text-muted-foreground line-through ml-2">₹{inrFull(p)}</span>
              <span className="text-xs text-muted-foreground ml-2">/ {p.duration}</span>
            </p>
            <ul className="mt-5 space-y-2">
              {p.features.map((f, j) => (
                <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Check size={14} className="text-gold mt-0.5 shrink-0" /> <E k={`pkg_${p.id}_feature_${j}`} f={f} />
                </li>
              ))}
            </ul>
            <span
              className={`mt-6 inline-flex items-center justify-center gap-2 w-full py-3 rounded-full text-[11px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${
                pkg === p.id ? "bg-gold text-black" : "border border-gold/50 text-gold"
              }`}
              data-testid={`package-select-cta-${p.id}`}
            >
              {pkg === p.id ? "✓ Selected" : "Select & Book"}
            </span>
          </motion.div>
        ))}
      </div>

      <div className="mt-16">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-2"><E k="readers_overline" f="Meet your readers" /></p>
        <h2 className="font-heading text-3xl font-semibold tracking-tight"><E k="readers_heading" f="Expert Tarot & Numerology readers, trusted by thousands." /></h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          <E k="readers_subtext" f="Handpicked, vetted and practising for years — not anonymous hotline voices. All three are trained masters of Tarot & Numerology. Read their credentials below, pick whoever you feel drawn to, and book instantly." />
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="readers-grid">
          {readers.map((r) => (
            <ReaderCard key={r.id} reader={r} selected={readerId === r.id} onSelect={() => setReaderId(r.id)} />
          ))}
        </div>
      </div>

      <div id="schedule" className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 scroll-mt-24">
        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4"><E k="booking_pick_day" f="Pick a day" /></h2>
          <div className="grid grid-cols-7 gap-2" data-testid="date-picker-grid">
            {days.map((d) => {
              const sel = date && d.toDateString() === date.toDateString();
              return (
                <button
                  key={d.toISOString()}
                  onClick={() => setDate(d)}
                  data-testid={`date-option-${d.toISOString().slice(0, 10)}`}
                  className={`p-2.5 border text-center transition-colors duration-300 ${
                    sel ? "border-gold bg-gold text-black" : "border-white/10 hover:border-gold/50"
                  }`}
                >
                  <span className="block text-[9px] uppercase tracking-wider opacity-70">
                    {d.toLocaleDateString("en", { weekday: "short" })}
                  </span>
                  <span className="block text-sm font-bold">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-8 mb-4"><E k="booking_pick_time" f="Pick a time (UTC)" /></h2>
          <div className="flex flex-wrap gap-2" data-testid="time-slot-grid">
            {slots.map((t) => {
              const booked = bookedTimes.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => setTime(t)}
                  disabled={booked}
                  data-testid={`time-slot-${t}`}
                  className={`px-5 py-2.5 border text-xs tracking-widest transition-colors duration-300 ${
                    booked
                      ? "border-white/5 text-muted-foreground/40 line-through cursor-not-allowed"
                      : time === t
                      ? "border-gold bg-gold text-black font-bold"
                      : "border-white/10 hover:border-gold/50"
                  }`}
                >
                  {booked ? `${t} · booked` : t}
                </button>
              );
            })}
          </div>
          {readerId && date && bookedTimes.length > 0 && (
            <p className="mt-2 text-[10px] text-muted-foreground" data-testid="booked-slots-note">
              Struck-out times are already booked for this reader on this day.
            </p>
          )}

          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-8 mb-4"><E k="booking_notes_heading" f="Anything the Oracle should know?" /></h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="A question, a situation, a feeling... (optional)"
            data-testid="booking-notes-input"
            className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold/60 transition-colors duration-300"
          />
        </div>

        <div className="glass-panel p-6 sm:p-8">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Session</span>
              <span data-testid="summary-package">{pkg ? packages.find((p) => p.id === pkg)?.name : "—"}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Reader</span>
              <span data-testid="summary-reader">{readerId ? readers.find((r) => r.id === readerId)?.name : "—"}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>When</span>
              <span data-testid="summary-datetime">{date ? `${date.toISOString().slice(0, 10)} · ${time || "—"}` : "—"}</span>
            </div>
            <div className="flex justify-between text-foreground font-bold pt-2 border-t border-white/10">
              <span>Total</span>
              <span className="text-gold" data-testid="summary-total">
                {(() => {
                  const p = packages.find((x) => x.id === pkg);
                  if (!p) return "—";
                  return (
                    <>
                      <span className="line-through text-muted-foreground text-xs mr-2">₹{inrFull(p)}</span>
                      ₹{inrDisc(p)}
                    </>
                  );
                })()}
              </span>
            </div>
          </div>

          {upi && (
            <div className="mt-6 border border-gold/30 bg-black/30 p-4 sm:p-5" data-testid="upi-panel">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-4">
                <E k="booking_pay_heading" f="Pay via UPI" />
                {pkg ? ` — ₹${inrDisc(packages.find((x) => x.id === pkg) || {})}` : ""}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
                <EImg
                  k="upi_qr_image"
                  f={upi.qr_image}
                  alt="UPI payment QR code"
                  testId="upi-qr-image"
                  wrapClassName="shrink-0"
                  className="w-48 sm:w-44 h-auto shrink-0 border border-white/15 bg-white p-1 rounded-md"
                />
                <div className="min-w-0 w-full">
                  <p className="text-sm text-foreground">{upi.payee}</p>
                  <p className="text-xs text-muted-foreground mt-1 break-all">
                    UPI ID: <span className="text-foreground" data-testid="upi-id-text">{upi.upi_id}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(upi.upi_id); toast.success("UPI ID copied"); }}
                    data-testid="upi-copy-button"
                    className="mt-2 inline-flex items-center px-3 py-1 border border-gold/40 rounded-full text-[10px] uppercase tracking-widest text-gold hover:bg-gold hover:text-black transition-colors duration-300"
                  >
                    Copy UPI ID
                  </button>
                  <ol className="mt-3 space-y-1.5 text-xs text-muted-foreground list-decimal list-inside">
                    <li><E k="upi_step_1" f="Scan the QR with any UPI app (GPay, PhonePe, Paytm)" /></li>
                    <li><E k="upi_step_2" f="Pay the exact discounted amount shown above" /></li>
                    <li><E k="upi_step_3" f="Attach your payment screenshot below & tap Book Now" /></li>
                  </ol>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-white/10">
                <p className="block text-[10px] uppercase tracking-[0.2em] font-bold text-gold mb-2">
                  <E k="upi_attach_label" f="Payment screenshot (required)" />
                </p>
                <label
                  htmlFor="upi-screenshot"
                  data-testid="upi-upload-label"
                  className="flex flex-col items-center justify-center gap-2 w-full cursor-pointer border-2 border-dashed border-gold/50 rounded-xl px-4 py-6 text-center hover:bg-gold/5 active:bg-gold/10 transition-colors duration-300"
                >
                  {screenshotFile ? (
                    <>
                      <img
                        src={URL.createObjectURL(screenshotFile)}
                        alt="Payment screenshot preview"
                        className="max-h-40 rounded-md border border-white/20"
                        data-testid="upi-screenshot-preview"
                      />
                      <p className="text-xs text-work" data-testid="upi-screenshot-attached">✓ {screenshotFile.name}</p>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tap to change</p>
                    </>
                  ) : (
                    <>
                      <Camera size={30} weight="duotone" className="text-gold" />
                      <p className="text-xs font-bold text-foreground">Tap to attach your payment screenshot</p>
                      <p className="text-[10px] text-muted-foreground">From your phone gallery or camera</p>
                    </>
                  )}
                  <input
                    id="upi-screenshot"
                    type="file"
                    accept="image/*"
                    data-testid="upi-screenshot-input"
                    onChange={(e) => setScreenshotFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          )}

          <button
            onClick={checkout}
            disabled={loading}
            data-testid="booking-checkout-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-gold text-black text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] disabled:opacity-50 transition-colors duration-300"
          >
            <CalendarCheck size={18} weight="bold" />
            {loading ? "Booking..." : !user ? "Sign in to Book" : "Book Now"}
          </button>
          <p className="mt-4 text-[10px] text-muted-foreground text-center"><E k="booking_footer_note" f="Booking confirmed after quick payment verification · Full refund up to 24h before" /></p>
        </div>
      </div>
    </div>
  );
}
